import { ChevronRight, Inbox, MessageSquare } from 'lucide-react'
import { GestorChamadosFilters } from '@/components/Gestores/GestorChamadosFilters'
import {
  GESTOR_TABLE_TH_CLASS,
  GESTOR_TABLE_TR_CLASS,
  GestorListTableShell,
} from '@/components/Gestores/GestorListTableShell'
import { FinanceChamadosWorkflow } from '@/components/Financeiro/FinanceChamadosWorkflow'
import { FinanceSlaBadge } from '@/components/Financeiro/FinanceSlaBadge'
import type { FinanceQueueLifecycle } from '@/components/Financeiro/financeQueueLifecycle'
import { FINANCE_QUEUE_LIFECYCLE_TABS } from '@/components/Financeiro/financeQueueLifecycle'
import { FINANCE_AREA_META, type FinanceAreaKey } from '@/components/Financeiro/solicitacoes/financeSolicitacoesConfig'
import { formatFinanceStatus } from '@/hooks/useFinanceiroPage'
import type { GestorChamadoRow } from '@/services/gestoresService'

type Props = {
  requests: GestorChamadoRow[]
  loading: boolean
  error: string | null
  typeLabels?: Record<string, string>
  queueLifecycle: FinanceQueueLifecycle
  setQueueLifecycle: (v: FinanceQueueLifecycle) => void
  queueSearch: string
  setQueueSearch: (v: string) => void
  queueArea: string
  setQueueArea: (v: string) => void
  onSelect: (id: string) => void
}

function formatTypeLabel(type: string, typeLabels?: Record<string, string>) {
  return typeLabels?.[type] ?? type.replace(/_/g, ' ')
}

function formatAreaLabel(area: string) {
  return FINANCE_AREA_META[area as FinanceAreaKey]?.label ?? area
}

function lifecycleTitle(lifecycle: FinanceQueueLifecycle): string {
  return FINANCE_QUEUE_LIFECYCLE_TABS.find((t) => t.id === lifecycle)?.label ?? 'Chamados'
}

function emptyMessage(lifecycle: FinanceQueueLifecycle, hasFilters: boolean): string {
  if (hasFilters) return 'Nenhum chamado encontrado com os filtros atuais.'
  if (lifecycle === 'historico') return 'Nenhum chamado concluído ainda.'
  if (lifecycle === 'cancelados') return 'Nenhum chamado cancelado.'
  if (lifecycle === 'pausados') return 'Nenhum chamado pausado.'
  return 'Abra uma solicitação na aba Solicitações. O protocolo e o andamento aparecem aqui.'
}

export function GestorChamadosTab({
  requests,
  loading,
  error,
  typeLabels,
  queueLifecycle,
  setQueueLifecycle,
  queueSearch,
  setQueueSearch,
  queueArea,
  setQueueArea,
  onSelect,
}: Props) {
  const hasFilters = Boolean(queueSearch.trim() || queueArea)
  const title = lifecycleTitle(queueLifecycle)

  return (
    <div className='space-y-4'>
      <header>
        <h2 className='text-xl font-semibold text-text-primary'>Meus chamados — {title}</h2>
        <p className='mt-2 text-sm text-text-muted'>
          Acompanhe as solicitações que você abriu para o financeiro e RH.
        </p>
      </header>

      <GestorChamadosFilters
        queueLifecycle={queueLifecycle}
        setQueueLifecycle={setQueueLifecycle}
        queueSearch={queueSearch}
        setQueueSearch={setQueueSearch}
        queueArea={queueArea}
        setQueueArea={setQueueArea}
      />

      <FinanceChamadosWorkflow variant='compact' />

      {error && (
        <div className='rounded-lg border border-danger/30 bg-danger/5 px-4 py-3 text-sm text-danger'>
          {error}
        </div>
      )}

      <GestorListTableShell
        loading={loading}
        loadingMessage='Carregando chamados…'
        empty={!loading && requests.length === 0}
        emptyIcon={
          <Inbox size={48} className='mx-auto mb-3 text-[var(--text-muted)] opacity-50' />
        }
        emptyTitle={`Nenhum chamado em ${title.toLowerCase()}`}
        emptyHint={emptyMessage(queueLifecycle, hasFilters)}
      >
        <table className='w-full'>
          <thead>
            <tr className='border-b border-[var(--glass-border)]'>
              {['Protocolo', 'Tipo', 'Área', 'Status', 'SLA', 'Aberto em', ''].map((h) => (
                <th
                  key={h || 'actions'}
                  className={GESTOR_TABLE_TH_CLASS}
                  style={{ fontSize: 'var(--font-size-label)' }}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {requests.map((r) => (
              <tr
                key={r.id}
                className={GESTOR_TABLE_TR_CLASS}
                onClick={() => onSelect(r.id)}
              >
                <td className='px-4 py-3'>
                  <span
                    className='font-mono font-medium text-[var(--font-color-body)]'
                    style={{ fontSize: 'var(--font-size-sm)' }}
                  >
                    {r.protocol}
                  </span>
                </td>
                <td className='px-4 py-3'>
                  <span
                    className='text-[var(--font-color-body)]'
                    style={{ fontSize: 'var(--font-size-sm)' }}
                  >
                    {formatTypeLabel(r.type, typeLabels)}
                  </span>
                  {r.assignee?.name && (
                    <p className='mt-0.5 text-[11px] text-[var(--font-color-muted)]'>
                      {r.assignee.name}
                    </p>
                  )}
                </td>
                <td className='px-4 py-3'>
                  <span
                    className='text-[var(--font-color-muted)]'
                    style={{ fontSize: 'var(--font-size-sm)' }}
                  >
                    {formatAreaLabel(r.area)}
                  </span>
                </td>
                <td className='px-4 py-3'>
                  <span className='rounded-md border border-[var(--brand-primary)]/20 bg-[var(--brand-primary)]/10 px-2 py-0.5 text-[9px] font-bold uppercase text-[var(--brand-primary)]'>
                    {formatFinanceStatus(r.status)}
                  </span>
                </td>
                <td className='px-4 py-3'>
                  <FinanceSlaBadge slaDueAt={r.slaDueAt} status={r.status} />
                </td>
                <td className='px-4 py-3'>
                  <span className='inline-flex items-center gap-1 text-[var(--font-color-muted)]' style={{ fontSize: 'var(--font-size-sm)' }}>
                    <MessageSquare size={12} aria-hidden />
                    {new Date(r.createdAt).toLocaleDateString('pt-BR')}
                  </span>
                </td>
                <td className='px-4 py-3'>
                  <ChevronRight size={14} className='text-[var(--text-muted)]' aria-hidden />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </GestorListTableShell>
    </div>
  )
}
